import { accountTrpcContract } from "@amtana/account-contract";
import type { Preferences, Profile, UpdateProfileInput, UploadAvatarInput } from "@amtana/account-contract";
import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";

// The signed-in person, for every browser module: profile, preferences. The
// plan, balances, limits and features belong to the organization now
// (ADR-003) and live in @amtana/billing-client.

/** The way to erase one's account, lent to the Terms gate (ADR-032, S37.07): My account sits behind it. */
export const EraseAccountPanel: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "account",
  "erase-panel",
);

export function useProfile(): QueryResult<Profile> {
  return useContractQuery(accountTrpcContract, "profile", { input: {} });
}

function useProfileWrite<TInput>(
  name: "updateProfile" | "uploadAvatar" | "removeAvatar" | "updatePreferences",
): MutationResult<Profile, TInput> {
  const mutation = useContractMutation(accountTrpcContract, name);
  const invalidate = useInvalidateContract();
  // The contract types `name`'s input and output; the hook restates them for consumers.
  const typed = mutation as unknown as MutationResult<Profile, TInput>;
  return {
    ...typed,
    run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate(accountTrpcContract)),
  };
}

export const useUpdateProfile = (): MutationResult<Profile, UpdateProfileInput> =>
  useProfileWrite("updateProfile");
/** Replaces the avatar; `input.dataUrl` comes straight from `FileReader.readAsDataURL`. */
export const useUploadAvatar = (): MutationResult<Profile, UploadAvatarInput> =>
  useProfileWrite("uploadAvatar");
export const useRemoveAvatar = (): MutationResult<Profile, Record<string, never>> =>
  useProfileWrite("removeAvatar");
export const useUpdatePreferences = (): MutationResult<Profile, Preferences> =>
  useProfileWrite("updatePreferences");
